import { useState, useEffect, useRef, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { ServerCog, ShieldCheck, Upload, Loader2, AlertTriangle, CheckCircle2, FileCode, Globe, Copy, Sparkles, Radar, ShieldAlert } from "lucide-react";
import Layout from "@/components/Layout";
import { useAuth } from "@/context/AuthContext";
import api from "@/lib/api";

const STATUS_STYLE = {
  vulnerable: { color: "#FF3B30", label: "Vulnerable" },
  unpinned: { color: "#FFD60A", label: "Unpinned" },
  ok: { color: "#30D158", label: "OK" },
};

export default function BackendScan() {
  const navigate = useNavigate();
  const { plan, isAdmin } = useAuth();
  const isAgency = plan === "agency" || isAdmin;

  // manifest analysis
  const [filename, setFilename] = useState("");
  const [content, setContent] = useState("");
  const [analyzing, setAnalyzing] = useState(false);
  const [result, setResult] = useState(null);
  const fileRef = useRef(null);

  // verification
  const [domain, setDomain] = useState("");
  const [verifying, setVerifying] = useState(false);
  const [checking, setChecking] = useState(false);
  const [verifyData, setVerifyData] = useState(null);
  const [verifications, setVerifications] = useState([]);

  // live backend probe (Phase 2)
  const [probing, setProbing] = useState(false);
  const [probeResult, setProbeResult] = useState(null);
  const [probeDomain, setProbeDomain] = useState("");

  const loadVerifications = useCallback(async () => {
    try {
      const res = await api.get("/verify");
      setVerifications(res.data.verifications || []);
    } catch {}
  }, []);

  useEffect(() => { if (isAgency) loadVerifications(); }, [isAgency, loadVerifications]);

  const onFile = (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    setFilename(f.name);
    const reader = new FileReader();
    reader.onload = () => setContent(String(reader.result || ""));
    reader.readAsText(f);
  };

  const analyze = async () => {
    if (!content.trim()) { toast.error("Upload or paste a manifest first"); return; }
    setAnalyzing(true); setResult(null);
    try {
      const res = await api.post("/deps/analyze", { filename, content });
      setResult(res.data);
      toast.success(`Analyzed ${res.data.total} dependencies`);
    } catch (e) {
      if (e?.response?.status === 402) { toast.error("Backend Scan requires an Agency plan."); navigate("/pricing"); }
      else toast.error(e?.response?.data?.detail || "Analysis failed");
    } finally { setAnalyzing(false); }
  };

  const startVerify = async () => {
    if (!domain.trim()) { toast.error("Enter a domain"); return; }
    setVerifying(true); setVerifyData(null);
    try {
      const res = await api.post("/verify/start", { domain });
      setVerifyData(res.data);
      if (res.data.verified) toast.success("Already verified");
    } catch (e) {
      if (e?.response?.status === 402) { toast.error("Backend Scan requires an Agency plan."); navigate("/pricing"); }
      else toast.error(e?.response?.data?.detail || "Could not start verification");
    } finally { setVerifying(false); }
  };

  const checkVerify = async () => {
    setChecking(true);
    try {
      const res = await api.post("/verify/check", { domain: verifyData?.domain || domain });
      if (res.data.verified) toast.success(`Verified via ${res.data.method.toUpperCase()}`);
      else toast.message(res.data.message || "Not verified yet");
      setVerifyData((v) => ({ ...v, verified: res.data.verified, method: res.data.method }));
      loadVerifications();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Check failed");
    } finally { setChecking(false); }
  };

  const copy = (t) => { navigator.clipboard?.writeText(t); toast.success("Copied"); };

  const runProbe = async (d) => {
    setProbing(true); setProbeResult(null); setProbeDomain(d);
    try {
      const res = await api.post("/backend/probe", { domain: d });
      setProbeResult(res.data);
      const crit = res.data.criticalCount || 0;
      if (crit > 0) toast.error(`${crit} critical exposure${crit > 1 ? "s" : ""} found on ${d}`);
      else toast.success(`Backend probe complete — no critical exposures on ${d}`);
    } catch (e) {
      if (e?.response?.status === 403) toast.error(e?.response?.data?.detail || "Verify the domain first.");
      else toast.error(e?.response?.data?.detail || "Probe failed");
    } finally { setProbing(false); }
  };

  if (!isAgency) {
    return (
      <Layout>
        <div className="max-w-2xl mx-auto mt-10 text-center bg-[#121212] border border-white/10 rounded-2xl p-10" data-testid="backend-scan-gate">
          <ServerCog size={40} className="text-[#4da3ff] mx-auto mb-4" />
          <h1 className="font-heading text-2xl font-bold text-white">Deep Backend Scan</h1>
          <p className="text-zinc-400 mt-3 text-sm leading-relaxed">
            Analyze your real backend dependencies for known vulnerabilities and end-of-life runtimes by uploading your
            <code className="mx-1 text-[#4da3ff]">package.json</code>, <code className="text-[#4da3ff]">requirements.txt</code>,
            <code className="mx-1 text-[#4da3ff]">Dockerfile</code> or SBOM — plus verify domain ownership. An Agency feature.
          </p>
          <button data-testid="upgrade-agency-btn" onClick={() => navigate("/pricing")} className="mt-6 inline-flex items-center gap-2 btn-brand text-white rounded-full px-5 py-2.5 font-medium">
            <Sparkles size={16} /> Upgrade to Agency
          </button>
        </div>
      </Layout>
    );
  }

  return (
    <Layout>
      <div className="mb-6">
        <h1 className="font-heading text-2xl font-bold text-white flex items-center gap-2"><ServerCog size={22} className="text-[#4da3ff]" /> Deep Backend Scan</h1>
        <p className="text-zinc-500 text-sm mt-1">Real dependency & runtime risk from your own manifests, plus a live backend probe on domains you've verified — no server credentials needed.</p>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-5 gap-6">
        {/* Manifest analysis */}
        <div className="lg:col-span-3 bg-[#121212] border border-white/10 rounded-xl p-5" data-testid="deps-card">
          <div className="flex items-center gap-2 mb-1"><FileCode size={18} className="text-[#4da3ff]" /><h2 className="font-heading text-lg font-semibold text-white">Dependency Analysis</h2></div>
          <p className="text-zinc-500 text-sm mb-4">Upload or paste <code className="text-[#4da3ff]">package.json</code>, <code className="text-[#4da3ff]">requirements.txt</code>, <code className="text-[#4da3ff]">Dockerfile</code>, or a CycloneDX/SPDX SBOM. We check every pinned dependency against the OSV vulnerability database.</p>

          <div className="flex flex-wrap items-center gap-3 mb-3">
            <input ref={fileRef} type="file" accept=".json,.txt,Dockerfile,.lock" onChange={onFile} className="hidden" data-testid="deps-file-input" />
            <button onClick={() => fileRef.current?.click()} className="flex items-center gap-2 bg-white/5 border border-white/15 hover:bg-white/10 text-white rounded-lg px-4 py-2 text-sm transition-colors" data-testid="deps-upload-btn">
              <Upload size={15} /> Upload file
            </button>
            {filename && <span className="text-xs text-zinc-400 font-mono">{filename}</span>}
          </div>

          <textarea
            data-testid="deps-textarea"
            value={content}
            onChange={(e) => setContent(e.target.value)}
            placeholder={'{\n  "dependencies": {\n    "react": "18.2.0",\n    "lodash": "4.17.11"\n  }\n}'}
            className="w-full h-40 bg-black/40 border border-white/10 rounded-lg p-3 text-xs font-mono text-zinc-200 focus:border-[#007AFF]/50 focus:outline-none resize-y"
          />

          <button onClick={analyze} disabled={analyzing} className="mt-3 flex items-center gap-2 btn-brand disabled:opacity-50 text-white rounded-lg px-5 py-2.5 font-medium transition-colors" data-testid="deps-analyze-btn">
            {analyzing ? <><Loader2 size={16} className="animate-spin" /> Analyzing…</> : <><ShieldCheck size={16} /> Analyze Dependencies</>}
          </button>

          {result && (
            <div className="mt-5" data-testid="deps-result">
              <div className="grid grid-cols-3 gap-3 mb-4">
                <Stat label="Dependencies" value={result.total} />
                <Stat label="Pinned" value={result.pinned} />
                <Stat label="Vulnerable" value={result.vulnerable} danger={result.vulnerable > 0} />
              </div>
              {result.runtimeFlags?.length > 0 && result.runtimeFlags.map((f, i) => (
                <div key={i} className="flex items-start gap-2 rounded-lg border border-[#FF3B30]/30 bg-[#FF3B30]/10 p-3 mb-3 text-sm" data-testid={`runtime-flag-${i}`}>
                  <AlertTriangle size={15} className="text-[#FF3B30] mt-0.5 shrink-0" />
                  <div><span className="text-white font-medium">{f.name}</span><p className="text-zinc-300 text-xs mt-0.5">{f.note}</p></div>
                </div>
              ))}
              <div className="overflow-x-auto rounded-lg border border-white/10">
                <table className="w-full text-sm">
                  <thead className="bg-white/5 text-zinc-400 text-xs uppercase tracking-wide">
                    <tr><th className="text-left px-3 py-2">Package</th><th className="text-left px-3 py-2">Version</th><th className="text-left px-3 py-2">Status</th><th className="text-left px-3 py-2">Vulnerabilities</th></tr>
                  </thead>
                  <tbody>
                    {result.findings.map((f, i) => {
                      const st = STATUS_STYLE[f.status] || STATUS_STYLE.ok;
                      return (
                        <tr key={i} className="border-t border-white/5" data-testid={`finding-${i}`}>
                          <td className="px-3 py-2 text-white break-all">{f.name} <span className="text-zinc-600 text-[10px]">{f.ecosystem}</span></td>
                          <td className="px-3 py-2 text-zinc-400 font-mono text-xs">{f.version || "—"}</td>
                          <td className="px-3 py-2"><span className="text-[10px] font-bold uppercase px-2 py-0.5 rounded" style={{ color: st.color, background: `${st.color}1a` }}>{st.label}</span></td>
                          <td className="px-3 py-2">
                            {f.vulnerabilities?.length ? (
                              <div className="flex flex-wrap gap-1">
                                {f.vulnerabilities.slice(0, 6).map((id) => (
                                  <a key={id} href={`https://osv.dev/vulnerability/${id}`} target="_blank" rel="noreferrer" className="text-[10px] font-mono text-[#4da3ff] underline hover:text-white">{id}</a>
                                ))}
                              </div>
                            ) : <span className="text-zinc-600 text-xs">None found</span>}
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
              <p className="text-[11px] text-zinc-600 mt-2">Vulnerability data via OSV.dev. "Unpinned" means no exact version was found to check.</p>
            </div>
          )}
        </div>

        {/* Domain verification */}
        <div className="lg:col-span-2 bg-[#121212] border border-white/10 rounded-xl p-5 h-fit" data-testid="verify-card">
          <div className="flex items-center gap-2 mb-1"><Globe size={18} className="text-[#4da3ff]" /><h2 className="font-heading text-lg font-semibold text-white">Verify Domain Ownership</h2></div>
          <p className="text-zinc-500 text-sm mb-4">Prove you control a domain to unlock deeper, authorized checks. Choose DNS or a file.</p>

          <div className="flex gap-2 mb-3">
            <input
              data-testid="verify-domain-input"
              value={domain}
              onChange={(e) => setDomain(e.target.value)}
              placeholder="example.com"
              className="flex-1 bg-black/40 border border-white/10 rounded-lg px-3 py-2 text-sm text-white font-mono focus:border-[#007AFF]/50 focus:outline-none"
            />
            <button onClick={startVerify} disabled={verifying} className="btn-brand disabled:opacity-50 text-white rounded-lg px-4 py-2 text-sm font-medium" data-testid="verify-start-btn">
              {verifying ? <Loader2 size={15} className="animate-spin" /> : "Start"}
            </button>
          </div>

          {verifyData && (
            <div className="rounded-lg border border-white/10 bg-black/30 p-4 space-y-3" data-testid="verify-instructions">
              {verifyData.verified ? (
                <div className="flex items-center gap-2 text-[#30D158] text-sm"><CheckCircle2 size={16} /> Verified via {verifyData.method?.toUpperCase()}</div>
              ) : (
                <>
                  <div>
                    <div className="text-xs text-zinc-400 mb-1">Option 1 — Add a DNS TXT record</div>
                    <div className="flex items-center gap-2 bg-black/50 rounded px-2 py-1.5">
                      <code className="text-[11px] text-[#4da3ff] break-all flex-1">{verifyData.instructions.dns.value}</code>
                      <button onClick={() => copy(verifyData.instructions.dns.value)} className="text-zinc-400 hover:text-white"><Copy size={13} /></button>
                    </div>
                  </div>
                  <div>
                    <div className="text-xs text-zinc-400 mb-1">Option 2 — Upload a file</div>
                    <div className="text-[11px] text-zinc-300 break-all">Put the token at <code className="text-[#4da3ff]">/.well-known/lwp-verify.txt</code></div>
                  </div>
                  <button onClick={checkVerify} disabled={checking} className="w-full flex items-center justify-center gap-2 bg-white/5 border border-white/15 hover:bg-white/10 text-white rounded-lg px-4 py-2 text-sm transition-colors" data-testid="verify-check-btn">
                    {checking ? <><Loader2 size={15} className="animate-spin" /> Checking…</> : <><ShieldCheck size={15} /> Check now</>}
                  </button>
                </>
              )}
            </div>
          )}

          {verifications.length > 0 && (
            <div className="mt-5" data-testid="verify-list">
              <div className="text-xs text-zinc-500 uppercase tracking-wide mb-2">Your domains</div>
              {verifications.map((v) => (
                <div key={v.domain} className="flex items-center justify-between py-1.5 border-t border-white/5 text-sm">
                  <span className="text-zinc-300 font-mono text-xs">{v.domain}</span>
                  <div className="flex items-center gap-2">
                    {v.verified
                      ? <span className="flex items-center gap-1 text-[#30D158] text-xs"><CheckCircle2 size={13} /> {v.method?.toUpperCase()}</span>
                      : <span className="text-zinc-600 text-xs">Unverified</span>}
                    {v.verified && (
                      <button
                        onClick={() => runProbe(v.domain)}
                        disabled={probing}
                        data-testid={`probe-btn-${v.domain}`}
                        className="flex items-center gap-1 bg-[#4da3ff]/10 border border-[#4da3ff]/30 hover:bg-[#4da3ff]/20 text-[#4da3ff] rounded-md px-2 py-1 text-[11px] font-medium transition-colors disabled:opacity-50"
                      >
                        {probing && probeDomain === v.domain ? <Loader2 size={12} className="animate-spin" /> : <Radar size={12} />} Probe
                      </button>
                    )}
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>

      {(probing || probeResult) && (
        <div className="mt-6 bg-[#121212] border border-white/10 rounded-xl p-5" data-testid="probe-result-card">
          <div className="flex items-center gap-2 mb-1">
            <Radar size={18} className="text-[#4da3ff]" />
            <h2 className="font-heading text-lg font-semibold text-white">Live Backend Probe</h2>
            {probeDomain && <span className="text-zinc-500 text-xs font-mono">— {probeDomain}</span>}
          </div>
          <p className="text-zinc-500 text-sm mb-4">Active, authorized fingerprint of hosting, runtime stack, live APIs and exposed sensitive files. Only runs on domains you've verified.</p>

          {probing && (
            <div className="flex items-center gap-2 text-zinc-400 text-sm py-6" data-testid="probe-loading">
              <Loader2 size={16} className="animate-spin" /> Probing {probeDomain}…
            </div>
          )}

          {probeResult && !probing && (
            <div data-testid="probe-result">
              <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 mb-4">
                <Stat label="Exposure score" value={probeResult.exposureScore} danger={probeResult.exposureScore < 60} />
                <Stat label="Critical exposures" value={probeResult.criticalCount} danger={probeResult.criticalCount > 0} />
                <Stat label="Live APIs" value={probeResult.apis?.length || 0} />
                <Stat label="Paths checked" value={probeResult.checkedPaths} />
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-3 mb-4">
                <div className="rounded-lg border border-white/10 bg-black/30 p-3 text-sm">
                  <div className="text-[11px] text-zinc-500 uppercase tracking-wide mb-2">Infrastructure</div>
                  <Row k="Hosting / CDN" v={probeResult.provider} />
                  <Row k="Server" v={probeResult.server} />
                  <Row k="Powered by" v={probeResult.poweredBy} />
                  <Row k="Detected stack" v={probeResult.stack?.length ? probeResult.stack.join(", ") : null} />
                </div>
                <div className="rounded-lg border border-white/10 bg-black/30 p-3 text-sm">
                  <div className="text-[11px] text-zinc-500 uppercase tracking-wide mb-2">Discovered APIs</div>
                  {probeResult.apis?.length ? probeResult.apis.map((a) => (
                    <div key={a.path} className="flex items-center gap-2 py-1 text-xs">
                      <CheckCircle2 size={13} className="text-[#4da3ff] shrink-0" />
                      <code className="text-[#4da3ff]">{a.path}</code>
                      <span className="text-zinc-500">— {a.title}</span>
                    </div>
                  )) : <div className="text-zinc-600 text-xs py-1">No public API endpoints detected.</div>}
                </div>
              </div>

              <div className="text-[11px] text-zinc-500 uppercase tracking-wide mb-2 flex items-center gap-1">
                <ShieldAlert size={13} /> Exposure findings ({probeResult.findings?.length || 0})
              </div>
              {probeResult.findings?.length ? probeResult.findings.map((f, i) => {
                const c = f.severity === "critical" ? "#FF3B30" : f.severity === "high" ? "#FF9F0A" : "#FFD60A";
                return (
                  <div key={i} className="rounded-lg bg-black/30 p-3 mb-2 border-l-4" style={{ borderColor: c }} data-testid={`probe-finding-${i}`}>
                    <div className="flex items-center gap-2">
                      <span className="text-[10px] font-bold uppercase px-2 py-0.5 rounded" style={{ color: c, background: `${c}1a` }}>{f.severity}</span>
                      <span className="text-white font-medium text-sm">{f.title}</span>
                      <code className="text-zinc-500 text-[11px] ml-auto">{f.path}</code>
                    </div>
                    <p className="text-zinc-300 text-xs mt-2">{f.detail}</p>
                    <p className="text-[#30D158] text-xs mt-1"><b>Fix:</b> {f.remediation}</p>
                  </div>
                );
              }) : (
                <div className="flex items-center gap-2 text-[#30D158] text-sm py-2" data-testid="probe-no-findings">
                  <CheckCircle2 size={15} /> No publicly exposed sensitive files or misconfigurations detected.
                </div>
              )}
              <p className="text-[11px] text-zinc-600 mt-2">Bounded, read-only probe of authorized domains. Engineering guidance, not legal advice.</p>
            </div>
          )}
        </div>
      )}
    </Layout>
  );
}

const Row = ({ k, v }) => (
  <div className="flex items-center justify-between py-1 text-xs border-t border-white/5 first:border-0">
    <span className="text-zinc-500">{k}</span>
    <span className="text-zinc-200 font-mono text-right">{v || "—"}</span>
  </div>
);

const Stat = ({ label, value, danger }) => (
  <div className="rounded-lg border border-white/10 bg-black/30 p-3 text-center">
    <div className={`text-2xl font-bold ${danger ? "text-[#FF3B30]" : "text-white"}`}>{value}</div>
    <div className="text-[11px] text-zinc-500 uppercase tracking-wide mt-0.5">{label}</div>
  </div>
);
